// 2026-05-26 - FormuLearning - Step 2 API 호출
// 2026-09-23 수정 - 이슈 #83: `predictPropertiesStream` 에 `signal`(Break·언마운트 시 abort).
// 2026-10-29 수정 - 이슈 #111: `_profile_quality`(M1016-11)·`_structure`(M1016-08) 메타 키 타입.
// 2026-11-03 수정 - 이슈 #112: Lab Data 사진 API(목록·업로드·Blob 조회·삭제·슬롯 삭제).
// 파일 위치: frontend/src/api/step2.ts

import client from './client'
import type { ApiResponse } from '../types/api'

export interface QTPPRow {
  attribute: string
  detail: string
  target: string
  rationale: string
}

// 2026-07-29 - 이슈 #51: OPERA 예측의 적용범위·신뢰도 지표.
// ai_prediction._quality 에 항목별로 담긴다. 밑줄 접두 키는 물성 항목이 아닌 메타다.
export interface PropertyQuality {
  ad: boolean | null          // 적용범위 내/외
  ad_index: number | null     // 적용범위 지수 0~1
  conf_index: number | null   // 신뢰도 지수 0~1
  range: [number, number] | null
  unit: string
}

/** Lipinski Rule of 5 항목별 판정. OPERA 는 위반 개수만 내므로 백엔드가 근거를 산출한다. */
export interface LipinskiRule {
  key: string
  label: string
  value: number
  limit: number
  unit: string
  violated: boolean
}

/** pH 프로파일 (M84-45) — 백엔드 `step2/ph_profile.py` 가 LogD 계열에서 유도한다.
 *  `solubility`·`s0` 는 LogP·수용해도가 모두 있을 때만 채워진다(없으면 null). 단위 g/L.
 *  `solubility` 배열 안의 null 은 표시 상한(`solubility_max`, 1,000 g/L) 이상이라 가린 점이다(M915-07, 이슈 #80). */
export interface PhProfile {
  ph: number[]
  logd: number[]
  solubility: (number | null)[] | null
  s0: number | null
  logp: number | null
  solubility_max?: number
}

/** pH 프로파일 신뢰도 — 국민대 `profile_reliable`(M1016-11). reasons = N 사유(AD 밖·pKa 예측 실패·반대이온 불일치). */
export interface ProfileQuality {
  reliable: boolean
  species_class: string
  reasons: string[]
}

/** 입력 구조 메타 — 입력형·모체 평균분자량(M1016-08). */
export interface StructureMeta {
  input_mw: number
  parent_mw: number
  is_salt: boolean
  counterions: string[]
  struct_flags: string[]
}

/** CATMoS 급성 경구 독성(#104) — OPERA 5종 + 묶음 지표. 물성 표 밖 독성 소절이 그린다. */
export interface ToxicityPrediction {
  vt: number | null
  nt: number | null
  epa: number | null
  ghs: number | null
  ld50: number
  ld50_range: [number, number] | null
  unit: string
  ad: boolean | null
  ad_index: number | null
  conf_index: number | null
}

/** 물성 값 + 메타 키. 밑줄 접두 키는 물성 항목이 아니므로 표에 렌더링하지 않는다. */
export interface AiPrediction {
  [key: string]: number | string | null | undefined | Record<string, PropertyQuality> | Record<string, string> | LipinskiRule[] | PhProfile | ProfileQuality | StructureMeta | ToxicityPrediction
  _quality?: Record<string, PropertyQuality>
  _lipinski?: LipinskiRule[]
  _ph_profile?: PhProfile
  _profile_quality?: ProfileQuality
  _structure?: StructureMeta
  _toxicity?: ToxicityPrediction
  _source?: string
  _errors?: Record<string, string>
}

export interface Step2Data {
  id: string
  project_id: string
  db_search: Record<string, number | string | null>
  ai_prediction: AiPrediction
  selected_source: Record<string, string>
  lab_data: Record<string, unknown>
  qtpp_rows: QTPPRow[]
  ai_summary: string | null
  ai_summary_ko: string | null
  created_at: string
  updated_at: string
}

export async function getStep2(projectId: string): Promise<Step2Data> {
  const { data } = await client.get<ApiResponse<Step2Data>>(`/step2/${projectId}/`)
  return data.data!
}

export async function updateStep2(projectId: string, body: Partial<Step2Data>): Promise<Step2Data> {
  const { data } = await client.put<ApiResponse<Step2Data>>(`/step2/${projectId}/`, body)
  return data.data!
}

export async function predictProperties(projectId: string, smiles: string): Promise<AiPrediction> {
  const { data } = await client.post<ApiResponse<{ predictions: AiPrediction }>>(`/step2/${projectId}/predict`, { smiles })
  return data.data!.predictions
}

export interface Step2StreamEvent {
  event: 'db_search' | 'ai_prediction' | 'summary' | 'error' | 'done'
  data: Record<string, unknown>
}

/** 물성 조회 점진 수신 (이슈 #51) — DB Search·AI Prediction 이 병렬로 돌고 먼저 끝난 것부터 도착한다.
 *  EventSource는 Authorization 헤더를 못 실어 fetch ReadableStream으로 파싱한다(Step 1과 동일 방식). */
export async function predictPropertiesStream(
  projectId: string,
  smiles: string,
  onEvent: (ev: Step2StreamEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const token = localStorage.getItem('access_token')
  const resp = await fetch(`/api/v1/step2/${projectId}/predict/stream`, {
    method: 'POST',
    credentials: 'include',
    signal,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ smiles }),
  })
  if (!resp.ok || !resp.body) {
    throw new Error(`predict stream failed: HTTP ${resp.status}`)
  }
  const reader = resp.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    let sep: number
    while ((sep = buffer.indexOf('\n\n')) !== -1) {
      const raw = buffer.slice(0, sep)
      buffer = buffer.slice(sep + 2)
      const line = raw.split('\n').find((l) => l.startsWith('data:'))
      if (!line) continue
      try {
        onEvent(JSON.parse(line.slice(5).trim()) as Step2StreamEvent)
      } catch {
        // 부분/비정상 청크는 무시
      }
    }
  }
}

export async function updateQtpp(projectId: string, qtppRows: QTPPRow[]): Promise<QTPPRow[]> {
  const { data } = await client.put<ApiResponse<{ qtpp_rows: QTPPRow[] }>>(`/step2/${projectId}/qtpp`, { qtpp_rows: qtppRows })
  return data.data!.qtpp_rows
}


// ── Lab Data 사진 (이슈 #112, M1016-04) ──
// 인증이 Bearer 헤더라 <img src> 로 직접 못 읽는다 — Blob 으로 받아 objectURL 로 보인다.

export interface LabImageMeta {
  id: string
  slot_key: string
  seq: number
  filename: string
  mime: string
  size_bytes: number
  width: number
  height: number
  created_at: string
}

export async function listLabImages(projectId: string): Promise<LabImageMeta[]> {
  const { data } = await client.get<ApiResponse<LabImageMeta[]>>(`/step2/${projectId}/lab-images`)
  return data.data ?? []
}

export async function uploadLabImage(projectId: string, slot: string, file: File): Promise<LabImageMeta> {
  const form = new FormData()
  form.append('file', file)
  const { data } = await client.post<ApiResponse<LabImageMeta>>(`/step2/${projectId}/lab-images`, form, {
    params: { slot },
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data.data!
}

export async function fetchLabImageBlob(projectId: string, imageId: string, kind: 'original' | 'thumbnail'): Promise<Blob> {
  const { data } = await client.get<Blob>(`/step2/${projectId}/lab-images/${imageId}/${kind}`, { responseType: 'blob' })
  return data
}

export async function deleteLabImage(projectId: string, imageId: string): Promise<void> {
  await client.delete(`/step2/${projectId}/lab-images/${imageId}`)
}

export async function deleteLabSlot(projectId: string, slot: string): Promise<void> {
  await client.delete(`/step2/${projectId}/lab-images`, { params: { slot } })
}
